// src/Dashboard/admin/sections/InstructorsSection.jsx
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, UserPlus, Trash2, ShieldCheck, ShieldOff, Settings } from "lucide-react";

import {
  listInstructors,
  updateInstructorStatus,
  deleteInstructor,
  getInstructor,
  setInstructorChurches,
} from "../../../services/adminApi";

import AddInstructorModal from "../../../components/AddInstructorModal";
import InstructorChurchesModal from "../components/InstructorChurchesModal";
import ScopeFilters from "../layout/ScopeFilters";
import { Badge } from "../components/ui";

/* ------------------------------ safe helpers ------------------------------ */
function safeId(x) {
  if (x === undefined || x === null) return "";
  return String(x);
}

function getIsActive(row) {
  // supports: isactive "1"/"0", is_active boolean/1/0, account_status "inactive"
  if (row?.isactive !== undefined) return String(row.isactive) === "1" ? "1" : "0";
  if (typeof row?.is_active === "boolean") return row.is_active ? "1" : "0";
  if (row?.is_active === 1 || row?.is_active === "1") return "1";
  const s = String(row?.account_status || "").toLowerCase();
  if (s === "inactive") return "0";
  return "1";
}

function getConferenceId(x) {
  return safeId(x?.id ?? x?.conference_id ?? x?.conferenceId);
}
function getStationId(x) {
  return safeId(x?.id ?? x?.station_id ?? x?.stationId);
}
function getDistrictId(x) {
  return safeId(x?.id ?? x?.district_id ?? x?.districtId);
}
function getChurchId(x) {
  return safeId(x?.id ?? x?.church_id ?? x?.churchId);
}

/** Derive instructor church id from various schemas */
function getInstructorChurchId(i) {
  return safeId(i?.church_id ?? i?.churchId ?? i?.church ?? i?.churchID);
}

/** Parse church ids list from getInstructor response (best-effort) */
function parseAssignedChurchIds(payload) {
  if (!payload) return [];
  const p = payload?.data ?? payload;
  const raw =
    p?.assignedChurchIds ??
    p?.churchIds ??
    p?.church_ids ??
    p?.churches ??
    p?.assigned_church_ids ??
    [];

  if (Array.isArray(raw)) {
    // if churches is array of objects -> map ids
    return raw
      .map((x) => (typeof x === "string" || typeof x === "number" ? x : x?.id ?? x?.church_id))
      .map((x) => safeId(x))
      .filter(Boolean);
  }
  return [];
}

/* ------------------------------- component -------------------------------- */

export default function InstructorsSection({
  // auth
  getToken,

  // hierarchy for filters + modals
  hierarchy = { conferences: [], stations: [], districts: [], churches: [] },

  // optional: show/hide scope box (usually true)
  showScopeFilters = true,
}) {
  // scope filters (hierarchy-based)
  const [selectedConference, setSelectedConference] = useState("all");
  const [selectedStation, setSelectedStation] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [selectedChurch, setSelectedChurch] = useState("all");

  // list controls
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // active | inactive | all
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // data
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // add instructor modal
  const [addOpen, setAddOpen] = useState(false);

  // churches modal
  const [churchModalOpen, setChurchModalOpen] = useState(false);
  const [churchModalErr, setChurchModalErr] = useState("");
  const [churchModalLoading, setChurchModalLoading] = useState(false);
  const [churchModalSearch, setChurchModalSearch] = useState("");
  const [activeInstructor, setActiveInstructor] = useState(null);
  const [selectedChurchIds, setSelectedChurchIds] = useState([]);

  const conferences = hierarchy?.conferences || [];
  const stations = hierarchy?.stations || [];
  const districts = hierarchy?.districts || [];
  const churches = hierarchy?.churches || [];

  // derive cascading availability like your AddInstructorModal
  const availableStations = useMemo(() => {
    if (selectedConference === "all") return [];
    return (stations || []).filter(
      (s) => String(s.conf_id ?? s.conference_id ?? s.conferenceId) === String(selectedConference) && getIsActive(s) !== "0"
    );
  }, [selectedConference, stations]);

  const availableDistricts = useMemo(() => {
    if (selectedStation === "all") return [];
    return (districts || []).filter(
      (d) => String(d.station_id ?? d.stationId) === String(selectedStation) && getIsActive(d) !== "0"
    );
  }, [selectedStation, districts]);

  const availableChurches = useMemo(() => {
    if (selectedDistrict === "all") return [];
    return (churches || []).filter(
      (c) => String(c.district_id ?? c.districtId) === String(selectedDistrict) && getIsActive(c) !== "0"
    );
  }, [selectedDistrict, churches]);

  const churchNameById = useMemo(() => {
    const m = new Map();
    (churches || []).forEach((c) => {
      const id = getChurchId(c);
      if (!id) return;
      m.set(String(id), c?.name || id);
    });
    return m;
  }, [churches]);

  async function withToken(fn) {
    const token = await getToken?.();
    if (!token) throw new Error("Missing auth token");
    return fn(token);
  }

  async function load(nextOffset = offset) {
    setLoading(true);
    setErr("");
    try {
      await withToken(async (token) => {
        const filter = {
          conferenceId: selectedConference !== "all" ? selectedConference : undefined,
          stationId: selectedStation !== "all" ? selectedStation : undefined,
          districtId: selectedDistrict !== "all" ? selectedDistrict : undefined,
          churchId: selectedChurch !== "all" ? selectedChurch : undefined,
          q: String(q || "").trim() || undefined,
          status: status || "all",
          // NOTE: adminApi buildFilterQuery currently doesn't include limit/offset; we paginate client-side for now
        };

        const res = await listInstructors(token, filter);
        const list = Array.isArray(res) ? res : res?.data || [];
        setRows(list);
        setOffset(nextOffset);
      });
    } catch (e) {
      setErr(e?.message || "Failed to load instructors");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // reload when scope filters change
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConference, selectedStation, selectedDistrict, selectedChurch, status]);

  async function toggleActive(row) {
    const id = safeId(row?.id);
    if (!id) return;
    const next = getIsActive(row) === "0";
    try {
      setLoading(true);
      await withToken((token) => updateInstructorStatus(token, id, next));
      await load(0);
    } catch (e) {
      setErr(e?.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  }

  async function onDelete(row) {
    const id = safeId(row?.id);
    if (!id) return;
    const ok = window.confirm("Delete this instructor? This cannot be undone.");
    if (!ok) return;

    try {
      setLoading(true);
      await withToken((token) => deleteInstructor(token, id));
      await load(0);
    } catch (e) {
      setErr(e?.message || "Failed to delete instructor");
    } finally {
      setLoading(false);
    }
  }

  async function openChurches(row) {
    setActiveInstructor(row);
    setChurchModalErr("");
    setChurchModalSearch("");
    setSelectedChurchIds([]);
    setChurchModalOpen(true);

    const id = safeId(row?.id);
    if (!id) return;

    try {
      setChurchModalLoading(true);
      await withToken(async (token) => {
        const res = await getInstructor(token, id);
        const ids = parseAssignedChurchIds(res);
        setSelectedChurchIds(ids);
      });
    } catch (e) {
      setChurchModalErr(e?.message || "Failed to load instructor details");
    } finally {
      setChurchModalLoading(false);
    }
  }

  async function saveChurches() {
    const id = safeId(activeInstructor?.id);
    if (!id) return;
    try {
      setChurchModalLoading(true);
      setChurchModalErr("");
      await withToken((token) => setInstructorChurches(token, id, selectedChurchIds));
      setChurchModalOpen(false);
      setActiveInstructor(null);
      await load(0);
    } catch (e) {
      setChurchModalErr(e?.message || "Failed to save churches");
    } finally {
      setChurchModalLoading(false);
    }
  }

  // very light client-side pagination (since adminApi doesn't support offset/limit yet)
  const paged = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const start = Math.max(0, offset);
    return list.slice(start, start + limit);
  }, [rows, offset]);

  const totalCount = (rows || []).length;

  return (
    <div className="space-y-4">
      {showScopeFilters && (
        <ScopeFilters
          show
          loading={loading}
          selectedConference={selectedConference}
          selectedStation={selectedStation}
          selectedDistrict={selectedDistrict}
          selectedChurch={selectedChurch}
          setSelectedConference={setSelectedConference}
          setSelectedStation={setSelectedStation}
          setSelectedDistrict={setSelectedDistrict}
          setSelectedChurch={setSelectedChurch}
          conferences={conferences}
          availableStations={availableStations}
          availableDistricts={availableDistricts}
          availableChurches={availableChurches}
          getConferenceId={getConferenceId}
          getStationId={getStationId}
          getDistrictId={getDistrictId}
          getChurchId={getChurchId}
          getIsActive={getIsActive}
        />
      )}

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold">Instructors</div>
            <div className="text-xs text-slate-500">
              Manage instructors in your scope (server enforces scope).
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => load(0)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              <UserPlus size={16} />
              Add
            </button>
          </div>
        </div>

        {err ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800">
            {err}
          </div>
        ) : null}

        <div className="mt-4 grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name/email/phone…"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
              />
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            >
              <option value="all">All</option>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>

          <div className="flex items-end justify-end">
            <button
              type="button"
              onClick={() => load(0)}
              className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              Go
            </button>
          </div>
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
              <tr>
                <th className="p-3 text-left">Instructor</th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-left">Church</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((i) => {
                const id = safeId(i?.id);
                const name = i?.full_name || i?.fullName || "—";
                const email = i?.email || "—";
                const phone = i?.phone || i?.phone_number || "—";
                const active = getIsActive(i) !== "0";
                const churchId = getInstructorChurchId(i);
                const churchLabel = churchId ? churchNameById.get(String(churchId)) || churchId : "—";

                return (
                  <tr
                    key={id || email}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3">
                      <div className="font-semibold">{name}</div>
                      <div className="text-xs text-slate-500 font-mono">{id || "—"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-slate-700 dark:text-slate-200">{email}</div>
                      <div className="text-xs text-slate-500">{phone}</div>
                    </td>
                    <td className="p-3">{churchLabel}</td>
                    <td className="p-3 text-center">
                      <Badge
                        tone={active ? "success" : "neutral"}
                        label={active ? "Active" : "Inactive"}
                        icon={active ? ShieldCheck : ShieldOff}
                      />
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openChurches(i)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-xs"
                          title="Manage churches"
                        >
                          <Settings size={14} />
                          Churches
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleActive(i)}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-xs"
                        >
                          {active ? "Deactivate" : "Activate"}
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(i)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 text-xs"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && paged.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-500">
                    No instructors found.
                  </td>
                </tr>
              ) : null}

              {loading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset <= 0}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Showing {Math.min(totalCount, offset + 1)}–{Math.min(totalCount, offset + limit)} of {totalCount}
          </span>
          <button
            type="button"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= totalCount}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Add Instructor */}
      <AddInstructorModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        hierarchy={hierarchy}
        defaultSelection={{
          conferenceId: selectedConference !== "all" ? selectedConference : "all",
          stationId: selectedStation !== "all" ? selectedStation : "all",
          districtId: selectedDistrict !== "all" ? selectedDistrict : "all",
          churchId: selectedChurch !== "all" ? selectedChurch : "all",
        }}
        onCreated={() => load(0)}
      />

      {/* Manage Instructor Churches */}
      <InstructorChurchesModal
        open={churchModalOpen}
        onClose={() => {
          setChurchModalOpen(false);
          setActiveInstructor(null);
        }}
        instructor={activeInstructor}
        loading={churchModalLoading}
        error={churchModalErr}
        churches={churches}
        selectedIds={selectedChurchIds}
        setSelectedIds={setSelectedChurchIds}
        search={churchModalSearch}
        setSearch={setChurchModalSearch}
        onSave={saveChurches}
        onClear={() => setSelectedChurchIds([])}
        getIsActive={getIsActive}
        getChurchId={getChurchId}
      />
    </div>
  );
}
